import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, signal, WritableSignal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { GbFlipListDirective } from '@galabid/shared/utils/directives'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'
import {
  PowerPledgeEntry,
  PowerPledgeWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbCurrencyPipe } from 'libs/shared/utils/pipes/src/lib/gb-currency-pipe/gb-currency.pipe'
import { GbColorService } from 'libs/shared/utils/services/src/lib/gb-color-service/gb-color.service'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map, shareReplay, startWith } from 'rxjs/operators'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { PRESENTER_POWER_PLEDGE_TEST_DONOR_TOTALS } from './gb-presenter-widget-power-pledge.test-donor-totals'

@Component({
  selector: 'gb-presenter-widget-power-pledge',
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    GbPresenterWidgetPlaceholderComponent,
    GbCurrencyPipe,
    GbFlipListDirective,
  ],
  templateUrl: './gb-presenter-widget-power-pledge.component.html',
  styleUrl: './gb-presenter-widget-power-pledge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetPowerPledgeComponent {
  private widgetSubject = new BehaviorSubject<PowerPledgeWidget | null>(null)

  @Input() set testData(testData: boolean) {
    this.testDataSignal.set(testData)
  }

  testDataSignal: WritableSignal<boolean> = signal(false)

  @Input() set powerPledgeWidget(value: PowerPledgeWidget) {
    this._powerPledgeWidget = value
    this.widgetSubject.next(value)
  }

  get powerPledgeWidget(): PowerPledgeWidget {
    return this._powerPledgeWidget
  }

  private _powerPledgeWidget!: PowerPledgeWidget

  powerPledgeWidget$: Observable<PowerPledgeWidget> = this.widgetSubject.pipe(
    filter((widget): widget is PowerPledgeWidget => !!widget),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  rows$ = this.powerPledgeWidget$.pipe(
    map((w) => Math.max(1, Number(w.rows || 10))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  donorTotals$ = combineLatest([toObservable(this.testDataSignal), this.powerPledgeWidget$, this.rows$]).pipe(
    map(([testData, widget, rows]) => {
      if (testData) {
        return PRESENTER_POWER_PLEDGE_TEST_DONOR_TOTALS.slice(0, rows)
      }
      return (widget.donor_totals || []).slice(0, rows)
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  showEmptyState$ = this.donorTotals$.pipe(
    map((rows) => !rows?.length),
    startWith(true),
  )

  constructor(
    private gbColorService: GbColorService,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  get fontSizeScale(): number {
    const fontSize = this.powerPledgeWidget.font_size ?? 12
    return fontSize / 12
  }

  get fontFamily(): string {
    return this.powerPledgeWidget.font_family || this.colorResolver.globalFontFamily() || ''
  }

  get fontWeight(): string {
    return this.powerPledgeWidget.font_weight || ''
  }

  get textAlign(): string {
    return this.powerPledgeWidget.text_align || 'center'
  }

  resolveColor(color: string | null | undefined): string {
    return this.colorResolver.resolve(color)
  }

  getContrastColor(bg: string | null | undefined): string {
    return this.gbColorService.getContrast(this.resolveColor(bg))
  }

  getRankBadgeColor(widget: PowerPledgeWidget, rank: number): string {
    if (rank === 1) return this.resolveColor(widget.first_place_color) || ''
    if (rank === 2) return this.resolveColor(widget.second_place_color) || ''
    if (rank === 3) return this.resolveColor(widget.third_place_color) || ''
    return this.resolveColor(widget.default_rank_color) || ''
  }

  trackEntry(_: number, entry: PowerPledgeEntry): string {
    return entry.uuid
  }
}
